//
// AHT Weight Management — Orlistat (Xenical®) shared screening downstream.
// Single source of truth for the post-confirmation steps shared by the initial
// supply and full re-screen pathways: PGD fresh checks, interaction checks,
// cautions review, supply attestation, and the shared outcomes.
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.

import type { OutcomeNode, PathwayNode } from "../../../types/pathway.types";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

export const PGD_FRESH_CHECKS = "orl-init-pgd-fresh-checks";
export const Q3_INT_CICLO     = "orl-init-int1-ciclosporin";
export const Q4_INT_WARF      = "orl-init-int2-warfarin";
export const Q5_INT_LEVO      = "orl-init-int3-levothyroxine";
export const Q6_INT_AED       = "orl-init-int4-antiepileptic";
export const Q7_CAUTIONS      = "orl-init-cautions";
export const Q8_ATTEST        = "orl-init-dose-confirm";

// ─────────────────────────────────────────────────────────────────────────────
// Outcome IDs
// ─────────────────────────────────────────────────────────────────────────────

export const OUT_NO_F2F        = "OUT_CANNOT_NOT_F2F";
export const OUT_CANNOT_SUPPLY = "OUT_CANNOT_SUPPLY";
export const OUT_SUPPLY        = "OUT_SUPPLY";

// ─────────────────────────────────────────────────────────────────────────────
// Shared downstream nodes — spread into a pathway's nodes array after the
// confirmation / exclusion-question stage. The first node is PGD_FRESH_CHECKS.
// ─────────────────────────────────────────────────────────────────────────────

export const orlistatScreeningDownstreamNodes: PathwayNode[] = [
  // ───────────────────────────────────────────────────────────────────────────
  // Step 2b — PGD inclusion / exclusion checks
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: PGD_FRESH_CHECKS,
    type: "pgd_exclusion_check",
    variant: "sequential",
    pgdIdOverride: "pgd-aht-wt-orlistat",
    anyTriggered: { outcomeId: OUT_CANNOT_SUPPLY },
    allCleared: Q3_INT_CICLO,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 3 — Interaction checks
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: Q3_INT_CICLO,
    type: "condition_route",
    conditions: [
      {
        evaluate: (ctx) => ctx.context["q17Ciclosporin"] === true,
        then: { outcomeId: OUT_CANNOT_SUPPLY },
      },
    ],
    default: Q4_INT_WARF,
  },

  {
    id: Q4_INT_WARF,
    type: "yes_no",
    text: "Is the patient taking warfarin or other oral anticoagulants?",
    hint: "Orlistat may increase anticoagulant effect — monitor INR.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document INR monitoring plan and frequency",
      acknowledgementText: "Patient and GP/anticoagulation clinic have been informed of enhanced INR monitoring",
    },
    yes: Q5_INT_LEVO,
    no: Q5_INT_LEVO,
    autoAnswer: { when: { hasMedication: ["warfarin", "acenocoumarol", "phenindione"] }, value: true },
  },

  {
    id: Q5_INT_LEVO,
    type: "yes_no",
    text: "Is the patient taking levothyroxine?",
    hint: "Reduced absorption — separate administration times; monitor thyroid function.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document levothyroxine administration timing (at least 4 hours before orlistat) and TSH monitoring plan",
      acknowledgementText: "Patient has been counselled on timing of administration and monitoring requirements",
    },
    yes: Q6_INT_AED,
    no: Q6_INT_AED,
    autoAnswer: { when: { hasMedication: ["levothyroxine"] }, value: true },
  },

  {
    id: Q6_INT_AED,
    type: "yes_no",
    text: "Is the patient taking antiepileptic drugs (valproate or lamotrigine)?",
    hint: "Reduced absorption — monitor seizure frequency and severity.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document seizure monitoring plan and frequency",
      acknowledgementText: "Patient and prescriber have been informed of monitoring requirements for seizure control",
    },
    yes: Q7_CAUTIONS,
    no: Q7_CAUTIONS,
    autoAnswer: { when: { hasMedication: ["valproate", "sodium valproate", "lamotrigine"] }, value: true },
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 4 — Cautions review
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: Q7_CAUTIONS,
    type: "flag",
    variant: "warn",
    message:
      "Step 3 — Review cautions before supply:\n" +
      "• High-fat diet substantially increases GI side effects — reinforce dietary guidance before supply\n" +
      "• Limit fat to ~30% of calories (~67g/day on 2,000 kcal diet) distributed across three meals\n" +
      "• CKD or volume depletion: oxalate nephropathy risk — advise adequate hydration\n" +
      "• Levothyroxine: reduced absorption — separate administration; monitor thyroid function\n" +
      "• T2DM on antidiabetics: weight loss may improve glycaemia — antidiabetic dose reduction may be required\n" +
      "• Fat-soluble vitamins (A, D, E, K): absorption reduced — advise multivitamin at bedtime (≥2 hours after orlistat)",
    next: Q8_ATTEST,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 5 — Supply attestation
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: Q8_ATTEST,
    type: "attestation",
    text: "Step 4 — Confirm supply of orlistat 120mg",
    attestationStatement:
      "I confirm that I have:\n" +
      "• Supplied 84 × orlistat 120mg capsules (28-day supply)\n" +
      "• Instructed: take immediately before, during, or within 1 hour of each main meal containing fat\n" +
      "• Instructed: omit dose if meal is missed or contains no fat\n" +
      "• Advised multivitamin supplement at bedtime or ≥2 hours after orlistat\n" +
      "• Warned: GI side effects (oily stools, urgency) are common — especially early and with high-fat meals\n" +
      "• Dietary guidance given: limit fat to ~30% of calories, distributed across three meals\n" +
      "• Recorded batch number and expiry date\n" +
      "• Advised patient to inform GP on initiation",
    conclusions: {
      optionA: { label: "Confirm and supply", next: { outcomeId: OUT_SUPPLY } },
      optionB: { label: "Do not supply",      next: { outcomeId: OUT_CANNOT_SUPPLY } },
    },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Shared outcomes — used by both the initial supply and re-screen pathways.
// ─────────────────────────────────────────────────────────────────────────────

export const orlistatScreeningOutcomes: Record<string, OutcomeNode> = {
  [OUT_NO_F2F]: {
    id: OUT_NO_F2F,
    type: "outcome",
    outcomeType: "cannot_supply",
    title: "Cannot supply — face-to-face consultation required",
    reason:
      "Initial supply under this PGD requires the patient to attend in person at AHT registered premises.",
    safetyNetting: [
      "Ask the patient to book an in-person appointment to proceed with initial supply.",
    ],
  },
  [OUT_CANNOT_SUPPLY]: {
    id: OUT_CANNOT_SUPPLY,
    type: "outcome",
    outcomeType: "cannot_supply",
    title: "Cannot supply — orlistat 120mg (Xenical®)",
    reason:
      "An inclusion criterion was not met, an exclusion criterion was triggered, or a contraindicated drug interaction was identified. Supply cannot proceed under this PGD.",
    safetyNetting: [
      "Discuss the reason supply cannot proceed with the patient.",
      "Consider referral to prescriber or GP if clinically appropriate.",
    ],
  },
  [OUT_SUPPLY]: {
    id: OUT_SUPPLY,
    type: "outcome",
    outcomeType: "supply",
    title: "Supply complete — orlistat 120mg (initial supply)",
    reason:
      "All inclusion criteria met, no exclusion criteria triggered, and supply attestation confirmed.",
    drugRef: "orlistat",
    safetyNetting: [
      "Return in 4 weeks for monthly monitoring review.",
      "Take orlistat immediately before, during, or within 1 hour of each main meal containing fat. Omit if meal is missed or fat-free.",
      "Take a multivitamin daily at bedtime (at least 2 hours after orlistat).",
      "GI side effects are proportional to fat intake — keep fat within limits to minimise symptoms.",
      "Discontinue if less than 5% weight loss after 12 weeks.",
    ],
  },
};
